import React, { useEffect, useState } from "react";
import { Repeat, Plus, RefreshCw, CalendarPlus, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, canFinancial } from "@/lib/permissions";
import { fmtMoney, todayISO } from "@/lib/format";
import { PageHeader, Loading, Field } from "@/components/common";
import { DataTable, Btn, TextInput, Select, Textarea } from "@/components/DataTable";
import { PriorityBadge } from "@/components/StatusBadge";
import { Modal } from "@/components/Modal";

const FREQS = ["One-off", "Weekly", "Fortnightly", "Monthly", "Quarterly", "Six Monthly", "Annual", "Custom"];
const PRIORITIES = ["Critical", "High", "Normal", "Low"];

export default function JobTemplates() {
  const { user } = useAuth();
  const fin = canFinancial(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refs, setRefs] = useState({ clients: [], sites: [], services: [], consultants: [], contracts: [], bus: [] });
  const [modal, setModal] = useState(false);
  const [freqModal, setFreqModal] = useState(null); // template
  const [form, setForm] = useState(null);

  const emptyForm = { job_title: "", service_type_id: "", client_id: "", site_id: "", contract_id: "", business_unit_id: "", frequency: "Monthly", custom_interval_days: 30, contract_start_date: todayISO(), contract_end_date: "", preferred_consultant_id: "", preferred_time: "09:00", priority: "Normal", standard_duration_mins: 120, standard_charge: 0, consultant_cost: 0, description: "", active: true };

  const load = async () => { setLoading(true); try { const { data } = await api.get("/job-templates"); setRows(data); } finally { setLoading(false); } };
  useEffect(() => {
    (async () => {
      const [clients, services, consultants, contracts, bus] = await Promise.all([
        api.get("/clients"), api.get("/service-types"), api.get("/consultants"), api.get("/contracts"), api.get("/business-units")]);
      setRefs((r) => ({ ...r, clients: clients.data, services: services.data, consultants: consultants.data, contracts: contracts.data, bus: bus.data }));
    })();
    load();
  }, []);

  const openNew = async () => { setForm(emptyForm); setModal(true); };
  useEffect(() => {
    if (form?.client_id) api.get(`/sites?client_id=${form.client_id}`).then((r) => setRefs((x) => ({ ...x, sites: r.data })));
  }, [form?.client_id]);

  const save = async () => {
    if (!form.job_title || !form.service_type_id || !form.client_id || !form.site_id) return toast.error("Title, service, client and site are required");
    try { await api.post("/job-templates", form); toast.success("Template created"); setModal(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const generate = async (t) => {
    try { const { data } = await api.post(`/job-templates/${t.id}/generate`); toast.success(`Generated ${data.created} visits (${data.total_occurrences} occurrences)`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "job_title", label: "Job Template", className: "font-medium text-slate-800", render: (r) => <div><div>{r.job_title}</div><div className="text-xs text-slate-400">{r.client_name} · {r.site_name}</div></div> },
    { key: "service_name", label: "Service", render: (r) => <span className="text-xs">{r.service_name}</span> },
    { key: "frequency", label: "Frequency", render: (r) => <span className="inline-flex items-center gap-1.5 text-xs font-medium text-seq-teal"><Repeat size={12} /> {r.frequency}</span> },
    { key: "priority", label: "Priority", render: (r) => <PriorityBadge priority={r.priority} /> },
    { key: "visit_count", label: "Visits", align: "right", render: (r) => <span className="font-medium">{r.visit_count}</span> },
    ...(fin ? [{ key: "standard_charge", label: "Charge", align: "right", render: (r) => fmtMoney(r.standard_charge) }] : []),
    { key: "actions", label: "", align: "right", render: (r) => (
      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {can(user, "schedule_jobs") && <button onClick={() => generate(r)} title="Generate visits" className="p-1.5 text-slate-400 hover:text-seq-green" data-testid={`generate-${r.id}`}><CalendarPlus size={15} /></button>}
        {can(user, "edit_job_templates") && <button onClick={() => setFreqModal(r)} title="Change frequency" className="p-1.5 text-slate-400 hover:text-seq-teal" data-testid={`freq-${r.id}`}><RefreshCw size={15} /></button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Job Templates" subtitle="Ongoing service requirements — the source of recurring scheduled visits" icon={Repeat}
        actions={can(user, "create_job_templates") && <Btn onClick={openNew} data-testid="new-template-btn"><Plus size={16} /> New Template</Btn>} />
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <Loading /> : <DataTable columns={columns} rows={rows} rowTestId="template-row" />}
      </div>

      {modal && form && (
        <Modal open onOpenChange={setModal} title="New job template" wide testId="template-modal"
          footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-template-btn">Save</Btn></>}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Job title" required className="sm:col-span-2"><TextInput value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></Field>
            <Field label="Service type" required><Select value={form.service_type_id} onChange={(e) => { const s = refs.services.find((x) => x.id === e.target.value); setForm({ ...form, service_type_id: e.target.value, standard_duration_mins: s?.default_duration_mins || 120, standard_charge: s?.default_charge || 0 }); }}><option value="">Select</option>{refs.services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
            <Field label="Business unit"><Select value={form.business_unit_id} onChange={(e) => setForm({ ...form, business_unit_id: e.target.value })}><option value="">Select</option>{refs.bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select></Field>
            <Field label="Client" required><Select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value, site_id: "" })}><option value="">Select</option>{refs.clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}</Select></Field>
            <Field label="Site" required><Select value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}><option value="">Select</option>{refs.sites.map((s) => <option key={s.id} value={s.id}>{s.site_name}</option>)}</Select></Field>
            <Field label="Frequency"><Select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>{FREQS.map((f) => <option key={f}>{f}</option>)}</Select></Field>
            <Field label="Priority"><Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</Select></Field>
            <Field label="Start date"><TextInput type="date" value={form.contract_start_date} onChange={(e) => setForm({ ...form, contract_start_date: e.target.value })} /></Field>
            <Field label="End date"><TextInput type="date" value={form.contract_end_date} onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })} /></Field>
            <Field label="Preferred consultant"><Select value={form.preferred_consultant_id} onChange={(e) => setForm({ ...form, preferred_consultant_id: e.target.value })}><option value="">Any</option>{refs.consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
            <Field label="Preferred time"><TextInput type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} /></Field>
            {fin && <>
              <Field label="Standard charge (€)"><TextInput type="number" value={form.standard_charge} onChange={(e) => setForm({ ...form, standard_charge: +e.target.value })} /></Field>
              <Field label="Consultant cost (€)"><TextInput type="number" value={form.consultant_cost} onChange={(e) => setForm({ ...form, consultant_cost: +e.target.value })} /></Field>
            </>}
            <Field label="Description" className="sm:col-span-2"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          </div>
        </Modal>
      )}

      {freqModal && <FrequencyModal template={freqModal} onClose={() => setFreqModal(null)} onApplied={() => { setFreqModal(null); load(); }} />}
    </div>
  );
}

function FrequencyModal({ template, onClose, onApplied }) {
  const [scope, setScope] = useState("template");
  const [newFreq, setNewFreq] = useState(template.frequency);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const scopeId = () => ({ template: template.id, client: template.client_id, site: template.site_id, service_type: template.service_type_id, contract: template.contract_id }[scope]);

  const runPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/frequency-change/preview", { scope, scope_id: scopeId(), new_frequency: newFreq }); setPreview(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try { const { data } = await api.post("/frequency-change/apply", { scope, scope_id: scopeId(), new_frequency: newFreq, confirm: true }); toast.success(`Applied: +${data.added} / -${data.removed} future visits. History untouched.`); onApplied(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onOpenChange={onClose} title="Change recurrence frequency" wide testId="frequency-modal"
      description="Only future visits change — historical completed work is never altered.">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Apply to">
          <Select value={scope} onChange={(e) => { setScope(e.target.value); setPreview(null); }} data-testid="freq-scope">
            <option value="template">This template only</option>
            <option value="client">Entire client</option>
            <option value="site">Entire site</option>
            <option value="service_type">Entire service type</option>
            {template.contract_id && <option value="contract">Entire contract</option>}
          </Select>
        </Field>
        <Field label="New frequency">
          <Select value={newFreq} onChange={(e) => { setNewFreq(e.target.value); setPreview(null); }} data-testid="freq-new">{FREQS.map((f) => <option key={f}>{f}</option>)}</Select>
        </Field>
      </div>
      <Btn variant="secondary" onClick={runPreview} disabled={busy} data-testid="freq-preview-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />} Preview changes</Btn>

      {preview && (
        <div className="grid grid-cols-3 gap-3 pt-1">
          <div className="rounded-lg bg-green-50 border border-green-200 p-3"><div className="text-2xl font-semibold text-green-700">{preview.added.length}</div><div className="text-xs text-green-700">Visits added</div></div>
          <div className="rounded-lg bg-red-50 border border-red-200 p-3"><div className="text-2xl font-semibold text-red-700">{preview.removed.length}</div><div className="text-xs text-red-700">Future visits removed</div></div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-2xl font-semibold text-slate-700">{preview.unchanged.length}</div><div className="text-xs text-slate-500">Unchanged</div></div>
        </div>
      )}
      {preview && (
        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn variant="danger" onClick={apply} disabled={busy} data-testid="freq-apply-btn">Confirm & apply</Btn>
        </div>
      )}
    </Modal>
  );
}
